import React, { useEffect, useRef } from "react";
import * as THREE from "three";

function createPatrolSphere(radius = 1.35) {
  const geo = new THREE.SphereGeometry(radius, 160, 160);
  const mat = new THREE.ShaderMaterial({
    uniforms: {
      uBands: { value: 12.0 },
      uPole: { value: new THREE.Vector3(0.55, -0.82, 0.15).normalize() },
    },
    vertexShader: `
      varying vec3 vNormalO;
      varying vec3 vViewN;
      void main() {
        vNormalO = normalize(normal);
        vViewN = normalize(normalMatrix * normal);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform float uBands;
      uniform vec3 uPole;
      varying vec3 vNormalO;
      varying vec3 vViewN;
      void main() {
        vec3 nObj = normalize(vNormalO);
        float ang = acos(clamp(dot(nObj, normalize(uPole)), -1.0, 1.0));
        float t = ang / 3.14159265;
        float bands = t * uBands;
        float shade = step(0.5, fract(bands));
        vec3 col = mix(vec3(0.02), vec3(0.94), shade);
        float facing = max(dot(normalize(vViewN), vec3(0.0, 0.0, 1.0)), 0.0);
        col *= 0.88 + 0.12 * facing;
        gl_FragColor = vec4(col, 1.0);
      }
    `,
  });
  return new THREE.Mesh(geo, mat);
}

function createFrame(size = 3.2) {
  const group = new THREE.Group();
  const stroke = 0.035;
  const arm = size * 0.12;
  const mat = new THREE.MeshBasicMaterial({ color: 0xf4f4f4 });
  const mk = (w, h, x, y) => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, stroke), mat);
    m.position.set(x, y, 0);
    group.add(m);
  };
  const half = size / 2;
  [
    [-1, 1],
    [1, 1],
    [-1, -1],
    [1, -1],
  ].forEach(([sx, sy]) => {
    mk(arm, stroke, sx * (half - arm / 2), sy * half);
    mk(stroke, arm, sx * half, sy * (half - arm / 2));
  });
  return group;
}

/**
 * @param {{ variant?: 'hero' | 'story', className?: string }} props
 */
export default function PatrolSphere({ variant = "hero", className = "" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(variant === "hero" ? 40 : 38, 1, 0.1, 100);
    let raf = 0;
    let disposed = false;

    if (variant === "hero") {
      renderer.setClearColor(0x0c0d10, 1);
      camera.position.set(0, 0.05, 5.4);

      const sphere = createPatrolSphere(1.85);
      const frame = createFrame(4.0);
      frame.position.z = 0.35;
      const root = new THREE.Group();
      root.add(sphere, frame);
      scene.add(root);

      const pointer = { x: 0, y: 0 };
      const onMove = (e) => {
        pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
        pointer.y = (e.clientY / window.innerHeight) * 2 - 1;
      };
      window.addEventListener("pointermove", onMove, { passive: true });

      const resize = () => {
        const w = canvas.clientWidth || window.innerWidth;
        const h = canvas.clientHeight || window.innerHeight;
        renderer.setSize(w, h, false);
        camera.aspect = w / Math.max(h, 1);
        camera.updateProjectionMatrix();
        const mobile = w < 720;
        root.position.set(mobile ? 0.7 : 1.85, mobile ? 0.05 : -0.05, 0);
        root.scale.setScalar(mobile ? 0.9 : 1.35);
      };
      resize();
      window.addEventListener("resize", resize);

      const clock = new THREE.Clock();
      const tick = () => {
        if (disposed) return;
        const t = clock.getElapsedTime();
        if (!reduce) {
          sphere.rotation.y = t * 0.12;
          sphere.rotation.x = 0.22;
          root.rotation.y = pointer.x * 0.06;
          root.rotation.x = pointer.y * -0.03;
        }
        renderer.render(scene, camera);
        raf = requestAnimationFrame(tick);
      };
      tick();

      return () => {
        disposed = true;
        cancelAnimationFrame(raf);
        window.removeEventListener("resize", resize);
        window.removeEventListener("pointermove", onMove);
        geoDispose(scene);
        renderer.dispose();
      };
    }

    // story variant
    renderer.setClearColor(0x101218, 1);
    camera.position.set(0, 0, 3.5);
    const sphere = createPatrolSphere(1.1);
    scene.add(sphere);
    const rings = [];
    for (let i = 0; i < 3; i += 1) {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(1.3 + i * 0.24, 0.007, 12, 140),
        new THREE.MeshBasicMaterial({
          color: 0x2f7f76,
          transparent: true,
          opacity: 0.32 - i * 0.07,
        })
      );
      ring.rotation.x = Math.PI / 2.35 + i * 0.12;
      scene.add(ring);
      rings.push(ring);
    }

    const resize = () => {
      const w = canvas.clientWidth || 600;
      const h = canvas.clientHeight || 420;
      renderer.setSize(w, h, false);
      camera.aspect = w / Math.max(h, 1);
      camera.updateProjectionMatrix();
    };
    resize();
    window.addEventListener("resize", resize);

    const clock = new THREE.Clock();
    const tick = () => {
      if (disposed) return;
      const t = clock.getElapsedTime();
      if (!reduce) {
        sphere.rotation.y = t * 0.18;
        sphere.rotation.z = -0.25 + Math.sin(t * 0.2) * 0.04;
        rings.forEach((r, i) => {
          r.rotation.z = t * (0.06 + i * 0.02);
        });
      }
      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      geoDispose(scene);
      renderer.dispose();
    };
  }, [variant]);

  return (
    <canvas
      ref={canvasRef}
      className={variant === "hero" ? `hero-canvas ${className}` : `story-canvas ${className}`}
      aria-hidden="true"
    />
  );
}

function geoDispose(scene) {
  scene.traverse((obj) => {
    if (obj.geometry) obj.geometry.dispose();
    if (obj.material) {
      if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose());
      else obj.material.dispose();
    }
  });
}
